import React, { useMemo } from 'react';
import {
  AccessTimeRounded,
  SecurityRounded,
  TwoWheelerRounded,
  VerifiedRounded
} from '@mui/icons-material';
import { Box, Button, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  calcEst,
  FONT_FAMILY,
  optionsRequired,
  showEst
} from '../common/sharedFunctions';
import { getLangKey } from 'common/src/other/getLangKey';

const accent = '#B7EE00';
const ink = '#111411';

const Section = ({ role }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = useSelector((state) => state.settingsdata.settings);
  const cartypes = useSelector((state) => state.cartypes);
  const auth = useSelector((state) => state.auth);
  const loggedIn = Boolean(auth.profile && auth.profile.uid);

  const data = useMemo(() => {
    return cartypes.cars
      ? [...cartypes.cars].sort((a, b) => a.pos - b.pos)
      : [];
  }, [cartypes.cars]);

  const bookingDestination =
    (role === 'driver') ||
    (calcEst && !showEst && !optionsRequired && (role === 'admin' || role === 'fleetadmin'))
      ? '/bookings'
      : '/addbookings';

  const formatAmount = (value) => {
    const number = parseFloat(value || 0);
    const locale = settings.country === 'Vietnam' ? 'vi-VN' : 'en-US';
    return number.toLocaleString(locale, {
      maximumFractionDigits: settings.decimal,
      minimumFractionDigits: settings.decimal
    });
  };

  const formatMoney = (value) => {
    const amount = formatAmount(value);
    return settings.swipe_symbol
      ? `${amount} ${settings.symbol}`
      : `${settings.symbol}${amount}`;
  };

  const handleBooking = (item) => {
    if (loggedIn) {
      navigate(bookingDestination, { state: { carData: item } });
    } else {
      navigate('/login');
    }
  };

  const serviceItems = [
    {
      description: t('product_section_1'),
      icon: <TwoWheelerRounded sx={{ fontSize: 27 }} />,
      title: t('pruduct_section_heading_1')
    },
    {
      description: t('product_section_2'),
      icon: <AccessTimeRounded sx={{ fontSize: 27 }} />,
      title: t('pruduct_section_heading_2')
    },
    {
      description: t('product_section_3'),
      icon: <SecurityRounded sx={{ fontSize: 27 }} />,
      title: t('pruduct_section_heading_3')
    },
    {
      description: t('product_section_4'),
      icon: <VerifiedRounded sx={{ fontSize: 27 }} />,
      title: t('pruduct_section_heading_4')
    }
  ];

  return (
    <>
      <Box
        component="section"
        sx={{
          backgroundColor: '#FFFFFF',
          py: { xs: 7, sm: 9, md: 11 }
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: { xs: 4, md: 5 }, textAlign: 'center' }}>
            <Typography sx={{
              color: '#687164',
              fontFamily: FONT_FAMILY,
              fontSize: '0.76rem',
              fontWeight: 900,
              letterSpacing: '0.13em',
              mb: 1.2,
              textTransform: 'uppercase'
            }}>
              {t('ride_options', { defaultValue: 'Ride options' })}
            </Typography>
            <Typography component="h2" sx={{
              color: ink,
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '2rem', sm: '2.65rem', md: '3.25rem' },
              fontWeight: 850,
              letterSpacing: '-0.04em',
              lineHeight: 1.05,
              mb: 1.5
            }}>
              {t('choose_your_ride', { defaultValue: 'Choose your ride' })}
            </Typography>
            <Typography sx={{
              color: '#667066',
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '0.94rem', sm: '1rem' },
              lineHeight: 1.7,
              margin: '0 auto',
              maxWidth: 620
            }}>
              {t('ride_options_subtitle', {
                defaultValue: 'Clear options, transparent starting rates, and a ride that fits your everyday journey.'
              })}
            </Typography>
          </Box>

          {data.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gap: { xs: 2, sm: 2.5 },
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(auto-fit, minmax(260px, 310px))'
              },
              justifyContent: 'center'
            }}>
              {data.map((item) => (
                <Box
                  key={item.id || item.name}
                  sx={{
                    backgroundColor: '#F6F8F2',
                    border: '1px solid #E2E7DE',
                    borderRadius: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    p: 1.25,
                    transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
                    '&:hover': {
                      borderColor: 'rgba(183,238,0,0.9)',
                      boxShadow: '0 22px 48px rgba(17,20,17,0.11)',
                      transform: 'translateY(-7px)'
                    }
                  }}
                >
                  <Box sx={{
                    alignItems: 'center',
                    background: 'linear-gradient(145deg, #FFFFFF 0%, #EDF1E8 100%)',
                    borderRadius: '18px',
                    display: 'flex',
                    height: 160,
                    justifyContent: 'center',
                    mb: 1,
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <Box sx={{
                      backgroundColor: accent,
                      borderRadius: '20px',
                      color: ink,
                      fontFamily: FONT_FAMILY,
                      fontSize: '0.66rem',
                      fontWeight: 900,
                      left: 12,
                      letterSpacing: '0.06em',
                      px: 1.2,
                      py: 0.6,
                      position: 'absolute',
                      textTransform: 'uppercase',
                      top: 12
                    }}>
                      {t('available', { defaultValue: 'Available' })}
                    </Box>
                    <Box
                      component="img"
                      src={item.image}
                      alt={t(getLangKey(item.name))}
                      sx={{
                        filter: 'drop-shadow(0 14px 18px rgba(17,20,17,0.16))',
                        height: 100,
                        objectFit: 'contain',
                        transition: 'transform 240ms ease',
                        width: 175
                      }}
                    />
                  </Box>

                  <Box sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                    <Typography sx={{
                      color: ink,
                      fontFamily: FONT_FAMILY,
                      fontSize: '1.15rem',
                      fontWeight: 850,
                      mb: 1.8
                    }}>
                      {t(getLangKey(item.name))}
                    </Typography>

                    <StackRate
                      label={t('starting_rate', { defaultValue: 'Starting rate' })}
                      value={`${formatMoney(item.rate_per_unit_distance)}/${settings.convert_to_mile ? t('mile') : t('km')}`}
                    />
                    <StackRate
                      label={t('min_fare')}
                      value={formatMoney(item.min_fare)}
                    />
                    {item.extra_info ? (
                      <Typography sx={{
                        color: '#6B736B',
                        fontFamily: FONT_FAMILY,
                        fontSize: '0.78rem',
                        lineHeight: 1.5,
                        mt: 1
                      }}>
                        {item.extra_info}
                      </Typography>
                    ) : null}

                    <Button
                      fullWidth
                      onClick={() => handleBooking(item)}
                      variant="contained"
                      sx={{
                        backgroundColor: ink,
                        borderRadius: '13px',
                        boxShadow: 'none',
                        color: '#FFFFFF',
                        fontFamily: FONT_FAMILY,
                        fontSize: '0.9rem',
                        fontWeight: 850,
                        mt: 2.2,
                        minHeight: 48,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: accent,
                          boxShadow: '0 12px 26px rgba(183,238,0,0.24)',
                          color: ink
                        }
                      }}
                    >
                      {bookingDestination === '/bookings' ? t('info') : t('book_now')}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{
              backgroundColor: '#F6F8F2',
              border: '1px dashed #C9D0C4',
              borderRadius: '22px',
              margin: '0 auto',
              maxWidth: 640,
              p: { xs: 4, sm: 5 },
              textAlign: 'center'
            }}>
              <TwoWheelerRounded sx={{ color: '#899287', fontSize: 42, mb: 1 }} />
              <Typography sx={{
                color: ink,
                fontFamily: FONT_FAMILY,
                fontSize: '1.2rem',
                fontWeight: 800
              }}>
                {t('service_start_soon')}
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          background: 'linear-gradient(135deg, #0B100D 0%, #121B2D 100%)',
          overflow: 'hidden',
          py: { xs: 7, sm: 9, md: 11 },
          position: 'relative'
        }}
      >
        <Box sx={{
          backgroundColor: 'rgba(183,238,0,0.08)',
          borderRadius: '50%',
          height: 430,
          position: 'absolute',
          right: -210,
          top: -190,
          width: 430
        }} />
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, position: 'relative' }}>
          <Box sx={{ mb: { xs: 4, md: 5 }, maxWidth: 720 }}>
            <Typography sx={{
              color: accent,
              fontFamily: FONT_FAMILY,
              fontSize: '0.76rem',
              fontWeight: 900,
              letterSpacing: '0.13em',
              mb: 1.2,
              textTransform: 'uppercase'
            }}>
              {t('why_laiwani', { defaultValue: 'Why Laiwani' })}
            </Typography>
            <Typography component="h2" sx={{
              color: '#FFFFFF',
              fontFamily: FONT_FAMILY,
              fontSize: { xs: '2rem', sm: '2.65rem', md: '3.25rem' },
              fontWeight: 850,
              letterSpacing: '-0.04em',
              lineHeight: 1.05,
              mb: 1.5
            }}>
              {t('made_for_everyday_rides', {
                defaultValue: 'Made for everyday rides around Shillong.'
              })}
            </Typography>
            <Typography sx={{
              color: 'rgba(255,255,255,0.66)',
              fontFamily: FONT_FAMILY,
              fontSize: '1rem',
              lineHeight: 1.7,
              maxWidth: 600
            }}>
              {t('why_laiwani_subtitle', {
                defaultValue: 'The details that matter—from booking to arrival—designed around a more dependable local journey.'
              })}
            </Typography>
          </Box>

          <Box sx={{
            display: 'grid',
            gap: { xs: 1.5, sm: 2 },
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }
          }}>
            {serviceItems.map((item, index) => (
              <Box
                key={item.title}
                sx={{
                  backgroundColor: index === 0 ? accent : 'rgba(255,255,255,0.065)',
                  border: index === 0
                    ? '1px solid rgba(183,238,0,0.95)'
                    : '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '22px',
                  minHeight: { xs: 210, sm: 230 },
                  p: { xs: 2.5, sm: 3 },
                  transition: 'background-color 220ms ease, transform 220ms ease',
                  '&:hover': {
                    backgroundColor: index === 0 ? '#C9FF19' : 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-5px)'
                  }
                }}
              >
                <Box sx={{
                  alignItems: 'center',
                  backgroundColor: index === 0 ? ink : 'rgba(183,238,0,0.12)',
                  borderRadius: '14px',
                  color: accent,
                  display: 'flex',
                  height: 50,
                  justifyContent: 'center',
                  mb: 3,
                  width: 50
                }}>
                  {item.icon}
                </Box>
                <Typography sx={{
                  color: index === 0 ? ink : '#FFFFFF',
                  fontFamily: FONT_FAMILY,
                  fontSize: '1.16rem',
                  fontWeight: 850,
                  mb: 1
                }}>
                  {item.title}
                </Typography>
                <Typography sx={{
                  color: index === 0 ? 'rgba(17,20,17,0.72)' : 'rgba(255,255,255,0.66)',
                  fontFamily: FONT_FAMILY,
                  fontSize: '0.88rem',
                  lineHeight: 1.65,
                  maxWidth: 420
                }}>
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

const StackRate = ({ label, value }) => (
  <Box sx={{
    alignItems: 'baseline',
    borderTop: '1px solid #E0E5DC',
    display: 'flex',
    gap: 1.5,
    justifyContent: 'space-between',
    py: 1
  }}>
    <Typography sx={{
      color: '#6B736B',
      fontFamily: FONT_FAMILY,
      fontSize: '0.78rem',
      fontWeight: 650
    }}>
      {label}
    </Typography>
    <Typography sx={{
      color: ink,
      fontFamily: FONT_FAMILY,
      fontSize: '0.9rem',
      fontWeight: 850,
      textAlign: 'right'
    }}>
      {value}
    </Typography>
  </Box>
);

export default Section;
